import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { Database } from '@copro/api';
import { useSpace } from '../../../hooks/useSpace';
import { keys as calendarKeys } from "../../calendar/api/keys";
import { keys as documentKeys } from "../../documents/api/keys";
import { syncOwnedDocument } from "../../documents/api/syncOwnedDocument";
import { meetingKeys } from './getMeetings';

type MeetingFields = Omit<Database['public']['Tables']['council_meetings']['Insert'], 'space_id'>;

type Variables = {
  command: MeetingFields;
  reportUrl: string | null;
};

async function postMeeting(command: MeetingFields, reportUrl: string | null, spaceId: string) {
  const { data, error } = await apiClient
    .from('council_meetings')
    .insert({ ...command, space_id: spaceId })
    .select()
    .single();

  if (error) {
    throw error;
  }

  // The report is stored as a document linked to the meeting.
  try {
    await syncOwnedDocument({
      currentDocumentId: null,
      fileUrl: reportUrl,
      name: command.title,
      type: 'Réunion CS',
      ownerColumn: 'meeting_id',
      ownerId: data.id,
      spaceId,
    });
  } catch (documentError) {
    // Roll back the meeting so a retry doesn't create a duplicate.
    await apiClient.from('council_meetings').delete().eq('id', data.id);
    throw documentError;
  }

  return data;
}

export function usePostMeeting() {
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  return useMutation({
    mutationFn: ({ command, reportUrl }: Variables) => postMeeting(command, reportUrl, currentSpace!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.list(currentSpace!.id) });
      queryClient.invalidateQueries({ queryKey: calendarKeys.list() });
      // The report also lives in documents (unified list).
      queryClient.invalidateQueries({ queryKey: documentKeys.documents });
    },
  });
}
